import { getToken } from '@luminovo/auth';
import { groupBy } from '@luminovo/commons';
import {
    ComplianceStatus,
    CustomOptionOfferLinkedPartType,
    http,
    NegotiationsCustomPartOfferBulkInputDTO,
    NegotiationsStandardPartOfferBulkInputDTO,
    PartLiteTypes,
    PriceType,
    QuantityUnit,
    StandardPartTypes,
} from '@luminovo/http-client';
import { useMutation } from '@tanstack/react-query';
import { OfferLineItem, ValidatedFormState } from '../../../types';
import { convertLeadTimeToDays } from '../../PdfViewer/model/RegionNetwork/parsers/LeadTime';
import { isCustomPartRow } from '../model';

export function useMutationSubmitQuote() {
    return useMutation({
        mutationFn: async ({ form }: { form: ValidatedFormState }) => {
            const { quoteRequest, rows } = form;

            await updatePartVotes(rows);

            const [customPartRows, nonCustomPartRows] = splitRows(rows, (row) => isCustomPartRow(row));

            await createCustomPartOffers(customPartRows, form);

            await createOffTheShelfOffers(nonCustomPartRows, form);
        },
    });
}

async function createCustomPartOffers(customPartRows: OfferLineItem[], form: ValidatedFormState) {
    if (customPartRows.length === 0) {
        return;
    }

    await http(
        'POST /quote-request/:id/add-offers/custom',
        {
            pathParams: { id: form.quoteRequest.id.toString() },
            requestBody: {
                inputs: customPartRows.map((row) => convertPdfOfferLineItemToCustomPartOfferInput(row, form)),
                event_metadata: { type: 'Excel' },
            },
        },
        getToken(),
    );
}

async function createOffTheShelfOffers(nonCustomPartRows: OfferLineItem[], form: ValidatedFormState) {
    if (nonCustomPartRows.length === 0) {
        return;
    }

    await http(
        'POST /quote-request/:id/add-offers/off-the-shelf',
        {
            pathParams: { id: form.quoteRequest.id.toString() },
            requestBody: {
                supplier_and_stock_location: form.quoteRequest.supplierAndStockLocation,
                inputs: nonCustomPartRows.map((row) => convertPdfOfferLineItemToOfferInput(row, form)),
                event_metadata: { type: 'Excel' },
            },
        },
        getToken(),
    );
}

function convertPdfOfferLineItemToCustomPartOfferInput(
    row: OfferLineItem,
    form: ValidatedFormState,
): NegotiationsCustomPartOfferBulkInputDTO {
    const cancellationWindowDays =
        convertLeadTimeToDays({
            value: row.cancellationWindow,
            unit: row.cancellationTimeUnit,
        }) ?? null;

    const currency = row.currency ?? form.defaultCurrency;

    const getLinkedPart = () => {
        if (row.part.kind === PartLiteTypes.CustomComponent) {
            return {
                type: CustomOptionOfferLinkedPartType.CustomComponent,
                id: row.part.id,
            };
        }
        if (row.part.kind === PartLiteTypes.Custom) {
            return {
                type: CustomOptionOfferLinkedPartType.CustomPart,
                id: row.part.id,
            };
        }

        throw new Error('Illegal state: part is not custom or custom component');
    };
    return {
        cancellation_window_in_days: cancellationWindowDays,
        linked_part: getLinkedPart(),
        supplier_and_stock_location: form.quoteRequest.supplierAndStockLocation,
        currency,
        notes: row.notes,
        one_time_costs:
            row.oneTimeCost?.map((cost) => ({
                amount: String(cost.amount),
                description: cost.label,
            })) ?? [],
        price_type: PriceType.QuotePrice,
        price_breaks: [
            {
                minimum_order_quantity: row.moq,
                minimum_packaging_quantity: row.mpq,
                amount: String(row.unitPrice),
            },
        ],
        unit_of_measurement: {
            unit: row.quoteRequestLineItem?.required_quantity.unit ?? QuantityUnit.Pieces,
            quantity: 1,
        },
        valid_from: row.validFrom ?? form.validFrom ?? null,
        valid_until: row.validUntil ?? form.validUntil ?? null,
        item_class: row.itemClass ?? null,
        offer_number: form.offerNumber,
        quote_request_line_item_id: row.quoteRequestLineItem?.id ?? null,
        bid: row.bid,
    };
}

async function updatePartVotes(rows: OfferLineItem[], setProgress: (progress: number) => void = () => {}) {
    const rowsByPartId = groupBy(rows, (row) => row.part.id);

    let progress = 0;
    const total = Object.keys(rowsByPartId).length;

    for (const partId in rowsByPartId) {
        const rows = rowsByPartId[partId];

        const reach = rows[0].reach;
        const rohs = rows[0].rohs;
        const eccnNumbers = rows[0].eccnNumbers;
        const htsCode = rows[0].htsCode;
        const countryOfOrigin = rows[0].countryOfOrigin;

        if (reach === ComplianceStatus.Unknown && rohs === ComplianceStatus.Unknown) {
            continue;
        }

        // We don't care about the result, we just want to know when it's done
        // if it fails, it will fail silently
        await http(
            'PATCH /parts/off-the-shelf/:partId',
            {
                pathParams: { partId },
                queryParams: {
                    rfq_context: 'OutsideRfQ',
                },
                requestBody: {
                    reach_compliant: reach === ComplianceStatus.Unknown ? undefined : reach,
                    rohs_compliant: rohs === ComplianceStatus.Unknown ? undefined : rohs,
                    eccn_numbers: eccnNumbers ? [eccnNumbers] : undefined,
                    hts_code: htsCode,
                    country_of_origin: countryOfOrigin ? [countryOfOrigin] : undefined,
                    origin: { type: 'Manual' as const },
                },
            },
            getToken(),
        ).catch(() => {
            // fail silently
        });

        progress++;
        setProgress(progress / total);
    }
}

function splitRows<T>(rows: T[], splitFunction: (row: T) => boolean): [whenTrue: T[], whenFalse: T[]] {
    const { true: whenTrue, false: whenFalse } = rows.reduce(
        (
            acc: {
                true: T[];
                false: T[];
            },
            row: T,
        ) => {
            acc[splitFunction(row) ? 'true' : 'false'].push(row);
            return acc;
        },
        { true: [], false: [] },
    );
    return [whenTrue, whenFalse];
}
function convertPdfOfferLineItemToOfferInput(
    offer: OfferLineItem,
    props: ValidatedFormState,
): NegotiationsStandardPartOfferBulkInputDTO {
    const { defaultCurrency } = props;

    const part = offer.part;
    const standardFactoryLeadTime = offer.standardFactoryLeadTime;
    const standardFactoryLeadTimeUnit = offer.standardFactoryLeadTimeUnit;

    const currency = offer.currency ?? defaultCurrency;

    const factoryLeadTimeDays = standardFactoryLeadTime
        ? convertLeadTimeToDays({
              value: standardFactoryLeadTime,
              unit: standardFactoryLeadTimeUnit,
          })
        : null;

    const cancelationWindowDays = convertLeadTimeToDays({
        value: offer.cancellationWindow,
        unit: offer.cancellationTimeUnit,
    });

    const priceBreakInput = {
        moq: offer.moq,
        mpq: offer.mpq,
        unit_price: offer.unitPrice,
        // the lead time is not set in the price break, instead it's set
        // as the factory lead time in availability_input
        lead_time_days: null,
    };

    const offerInput: NegotiationsStandardPartOfferBulkInputDTO = {
        quote_request_line_item_id: offer.quoteRequestLineItem?.id ?? null,
        supplier_part_number: offer.supplierPartNumber ?? '',
        one_time_costs: offer.oneTimeCost
            ? offer.oneTimeCost.map((cost) => ({
                  amount: String(cost.amount),
                  description: cost.label,
              }))
            : [],
        valid_until: offer.validUntil ?? props.validUntil ?? null,
        cancellation_window_in_days: cancelationWindowDays ?? null,
        item_class: offer.itemClass ?? null,
        valid_from: offer.validFrom ?? props.validFrom ?? null,
        attachment: props.attachment ?? null,

        notes: offer.notes,
        currency,
        price_type: PriceType.QuotePrice,
        packaging: offer.packaging ?? null,
        availability_input: {
            stock: offer.stock ?? null,
            factory_lead_time_days: factoryLeadTimeDays ?? null,
            factory_quantity: null,
            on_order: [],
        },
        supplier_and_stock_location: props.quoteRequest.supplierAndStockLocation,

        unit_of_measurement: {
            unit: offer.quoteRequestLineItem?.required_quantity.unit ?? QuantityUnit.Pieces,
            quantity: 1,
        },
        linked_part: {
            type: StandardPartTypes.OffTheShelf,
            data: part.id,
        },
        price_break_inputs: [priceBreakInput],
        ncnr: offer.ncnr ?? null,
        offer_number: props.offerNumber,
        bid: offer.bid,
    };

    return offerInput;
}
